import { alpha, Theme, ThemeOptions } from '@mui/material';

import { capitalDesignTokens } from '../../capital_design_tokens';

const getAlertColors = (theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';
  const makeColors = (severity: 'info' | 'success' | 'warning' | 'error') => {
    const palette = theme.palette[severity];
    return {
      // In dark mode use main (a lighter accessible shade); in light mode use dark (high contrast)
      dark: isDark ? palette.main : palette.dark,
      // In dark mode use a translucent tint; in light mode use the *50 pastel
      light: isDark ? alpha(palette.main, 0.12) : palette.light
    };
  };
  return {
    info: makeColors('info'),
    success: makeColors('success'),
    warning: makeColors('warning'),
    error: makeColors('error')
  };
};

export const MuiAlertTitle: ThemeOptions['components']['MuiAlertTitle'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      fontSize: capitalDesignTokens.foundations.typography.fontSize.h5,
      fontWeight: capitalDesignTokens.foundations.typography.fontWeight.semiBold,
      margin: theme.spacing(0)
    })
  }
};

declare module '@mui/material/Alert' {
  // tslint:disable-next-line:interface-name
  interface AlertPropsVariantOverrides {
    condensed: true;
  }
}

export const MuiAlert: ThemeOptions['components']['MuiAlert'] = {
  defaultProps: {
    variant: 'filled'
  },
  variants: [
    {
      props: { variant: 'condensed' },
      style: ({ theme, ownerState }: { theme: Theme; ownerState: { severity: keyof ReturnType<typeof getAlertColors> } }) => {
        const colors = getAlertColors(theme);
        return {
          padding: theme.spacing(0, 2),
          borderColor: colors[ownerState.severity].dark,
          backgroundColor: colors[ownerState.severity].light,
          '>.MuiAlert-icon': {
            fontSize: theme.typography.htmlFontSize,
            padding: theme.spacing(1, 0)
          },
          '>.MuiAlert-action': {
            padding: theme.spacing(0, 1),
            svg: {
              fontSize: theme.typography.htmlFontSize
            }
          },
          '>.MuiAlert-message': {
            padding: theme.spacing(1, 0),
            display: 'flex',
            alignItems: 'baseline',
            '>.MuiAlertTitle-root': {
              color: colors[ownerState.severity].dark,
              marginRight: theme.spacing(0.5)
            }
          }
        };
      }
    }
  ],
  styleOverrides: {
    root: ({ theme, ownerState }) => {
      const colors = getAlertColors(theme);
      return {
        padding: theme.spacing(2),
        color: theme.palette.text.primary,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: theme.spacing(0.5),
        '&.MuiAlert-filled': {
          backgroundColor: colors[ownerState.severity].light,
          borderColor: colors[ownerState.severity].dark
        },
        '&.MuiAlert-filled >.MuiAlert-message .MuiAlertTitle-root': {
          color: colors[ownerState.severity].dark
        },
        '&>.MuiAlert-icon': {
          alignItems: 'center',
          fontSize: theme.typography.htmlFontSize * 2,
          color: colors[ownerState.severity].dark
        },
        '&>.MuiAlert-action': {
          alignItems: 'center',
          padding: theme.spacing(0, 1),
          svg: {
            fontSize: theme.typography.htmlFontSize * 1.5,
            color: theme.palette.text.secondary
          }
        },
        '&>.MuiAlert-message': {
          padding: theme.spacing(0)
        },
        '&.MuiButtonBase-root': {
          padding: theme.spacing(1)
        }
      };
    }
  }
};
